import '@expo/match-media';
import React, { useState } from 'react';
import {
  Image,
  ScrollView,
  ScrollViewProps,
  StyleProp,
  StyleSheet,
  useWindowDimensions,
  View,
  ViewProps,
  ViewStyle,
} from 'react-native';
import { useMediaQuery } from 'react-responsive';
import { Dimensions } from 'react-native';

import styled from 'styled-components/native';
import { images } from '../assets/images';
import { colors, dimensions, scale } from '../Styles';
import { Auth0Context, useAuth0 } from './Auth0Provider';
import { DrawerContents } from './DrawerContents';
import { MNOHSHeader } from './MNOHSHeader';
import { MNOHSSafeView } from './MNOHSSafeView';

// Not typed unfortunately, and other Drawer components
// weren't working on web
const { Drawer } = require('react-native-material-drawer');

const { width, height } = Dimensions.get('window');

type Props = {
  /** Whether or not to show header */
  headerShown?: boolean;
  /** Header title */
  headerTitle: string;
  /** If the responsive container (child container) should have a padding */
  padded?: boolean;
  /** Component to be displayed as a parent container */
  parentComponentType?: 'ScrollView' | 'View';
  /** Whether or not the component should have a header (with logo) */
  hasHeader?: boolean;
  /** As the name implies - only applicable if parent component is ScrollView */
  scrollViewProps?: ScrollViewProps;
  /** As the name implies - only applicable if parent component is View */
  viewProps?: ViewProps;
  /** Custom styling of childContainer */
  childContainerStyle?: StyleProp<ViewStyle>;
};

export const MNOHSDashboardWrapper: React.FC<Props> = ({
  headerShown = true,
  headerTitle,
  padded,
  parentComponentType = 'ScrollView',
  hasHeader = true,
  scrollViewProps,
  viewProps,
  childContainerStyle,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const { isAuthenticated, logout } = useAuth0() as Auth0Context;
  const isMobile = useMediaQuery({ maxDeviceWidth: 768 });
  const isDesktop = useMediaQuery({ minDeviceWidth: 992 });

  return (
    <MNOHSSafeView>
      <Container>
        {hasHeader && (
          <View style={styles.header}>
            <LogoContainer small={isDesktop && hasHeader}>
              <Image
                source={images.MNOHSLogo}
                style={styles.logo}
                resizeMode="contain"
                accessibilityLabel="Minnesota Online High School"
              />
            </LogoContainer>
          </View>
        )}
        <Body style={styles.body} small={isDesktop && hasHeader}>
          <Drawer
            width={scale(240)}
            open={isMobile || !isAuthenticated ? isOpen : true}
            type={isMobile ? 'modal' : 'permanent'}
            animationTime={isMobile ? 200 : 0}
            onClose={() => setIsOpen(false)}
            drawerContent={<DrawerContents logout={() => logout({ returnTo: window.location.origin })} />}>
            {/* header */}
            {headerShown && (
              <MNOHSHeader
                burgerHidden={!isMobile}
                headerTitle={headerTitle}
                userPressedBurgerMenu={() => setIsOpen(true)}
              />
            )}
            {/* list of students */}
            <BodyContent full={!hasHeader}>
              {parentComponentType === 'ScrollView' ? (
                <ScrollView style={styles.parentContainer} {...scrollViewProps}>
                  <View
                    accessible={true}
                    style={[
                      styles.childContainer,
                      !isMobile && { width: scale(dimensions.WEB_MAX_CONTAINER_WIDTH) },
                      padded && { padding: scale(20) },
                      childContainerStyle,
                    ]}>
                    {children}
                  </View>
                </ScrollView>
              ) : (
                <View style={styles.parentContainer} {...viewProps}>
                  <View
                    accessible={true}
                    style={[
                      styles.childContainer,
                      !isMobile && { width: scale(dimensions.WEB_MAX_CONTAINER_WIDTH) },
                      padded && { padding: scale(20) },
                      childContainerStyle,
                    ]}>
                    {children}
                  </View>
                </View>
              )}
            </BodyContent>
          </Drawer>
        </Body>
      </Container>
    </MNOHSSafeView>
  );
};

const c = {
  HEADER_BORDER: '#eb0008',
  HEADER_BG: '#f2f0ee',
};

const styles = StyleSheet.create({
  header: {
    borderBottomWidth: 2,
    borderBottomColor: c.HEADER_BORDER,
    backgroundColor: c.HEADER_BG,
    zIndex: 1,
  },
  logo: {
    height: scale(58),
    width: scale(256),
    margin: scale(4),
    aspectRatio: 1,
    mixBlendMode: 'multiply',
  },
  body: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.125,
    shadowRadius: 30,
  },
  // container of the parent content
  parentContainer: {
    backgroundColor: colors.grayLight,
    flex: 1,
  },
  // container of the child responsive content
  childContainer: {
    flex: 1,
    alignSelf: 'center',
    width: '100%',
    marginBottom: scale(32),
  },
});

const Container = styled.View`
  overflow-y: hidden;
  height: 100vh;
`;

const Body = styled.View<{ small: boolean }>`
  min-width: ${({ small }) => (small ? `${scale(992)}px` : '100%')};
  align-self: center;
`;

const LogoContainer = styled.View<{ small: boolean }>`
  min-width: ${({ small }) => (small ? `${scale(992)}px` : '100%')};
  align-self: center;
  background-color: ${c.HEADER_BG};
`;

const BodyContent = styled.View<{ full: boolean; vh: number }>`
  height: ${({ full, vh }) => (full ? '100vh' : `${height - scale(132)}px`)};
`;
